import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { compressImage, fileToDataUrl } from "@/lib/imageUtils";
import { Camera, Upload, X, RotateCcw } from "lucide-react";

export function CameraCapture({ mode = "photo", onCapture, onClose, valid }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        setError("Camera unavailable — use file upload instead.");
      }
    }
    start();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const raw = canvas.toDataURL("image/jpeg", 0.9);
    const compressed = await compressImage(raw);
    setPreview(compressed);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const raw = await fileToDataUrl(f);
    const compressed = await compressImage(raw);
    setPreview(compressed);
  };

  const confirm = () => {
    onCapture(preview);
    setPreview(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950" data-testid="camera-capture">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <span className="overline text-slate-300">
          {mode === "scan" ? "Scan Serial" : "Capture Photo"}
        </span>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="camera-close" className="text-white">
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        {preview ? (
          <img src={preview} alt="capture preview" className="max-h-full max-w-full object-contain" />
        ) : error ? (
          <div className="p-8 text-center text-slate-400">{error}</div>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
            {mode === "scan" && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                  className={`relative h-[220px] w-[300px] max-w-[85%] overflow-hidden rounded-lg border-2 ${
                    valid === true ? "border-emerald-500" : valid === false ? "border-red-500" : "border-blue-500"
                  }`}
                >
                  <div className="scan-line absolute left-0 top-0 h-0.5 w-full bg-blue-400/80 shadow-[0_0_12px_2px_rgba(59,130,246,0.7)]" />
                  {["top-0 left-0 border-l-4 border-t-4", "top-0 right-0 border-r-4 border-t-4", "bottom-0 left-0 border-l-4 border-b-4", "bottom-0 right-0 border-r-4 border-b-4"].map((c, i) => (
                    <div key={i} className={`absolute h-6 w-6 ${c} ${valid === true ? "border-emerald-400" : "border-blue-400"}`} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 border-t border-slate-800 p-4">
        {preview ? (
          <>
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="camera-retake" className="gap-2 border-slate-700">
              <RotateCcw className="h-4 w-4" /> Retake
            </Button>
            <Button onClick={confirm} data-testid="camera-confirm" className="gap-2 bg-emerald-600 hover:bg-emerald-500 px-8">
              Use This
            </Button>
          </>
        ) : (
          <>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="camera-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="camera-upload" className="gap-2 border-slate-700">
              <Upload className="h-4 w-4" /> Upload
            </Button>
            {!error && (
              <Button onClick={snap} data-testid="camera-snap" className="h-16 w-16 rounded-full bg-blue-600 hover:bg-blue-500">
                <Camera className="h-7 w-7" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
